import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ProfilePhoto } from '@/types/models';

export interface PhotoLike {
  id: string;
  fromUid: string;
  toUid: string;
  photoId: string;
  createdAt: string;
}

/** Number of most recent received likes visible to non-subscribers. */
export const FREE_LIKE_COUNT = 3;

/**
 * Deterministic doc id: one like per (liker, photo) pair, so creating the
 * same like twice is idempotent and a `getDoc` answers "did I already like".
 */
export function photoLikeId(toUid: string, fromUid: string, photoId: string): string {
  return `${toUid}_${fromUid}_${photoId}`;
}

/**
 * Photos without a stored id fall back to `id === url` in `normalizePhotos`
 * (legacy shape). Their urls contain characters we don't want in doc ids and
 * aren't stable across re-uploads, so they can't be liked.
 */
export function isLikeablePhoto(photo: ProfilePhoto): boolean {
  return Boolean(photo.id) && photo.id !== photo.url;
}

export const likeService = {
  async likePhoto(fromUid: string, toUid: string, photo: ProfilePhoto): Promise<void> {
    if (!isLikeablePhoto(photo)) return;
    const id = photoLikeId(toUid, fromUid, photo.id);
    await setDoc(doc(db, 'photoLikes', id), {
      fromUid,
      toUid,
      photoId: photo.id,
      createdAt: new Date().toISOString(),
    });
  },

  async unlikePhoto(fromUid: string, toUid: string, photoId: string): Promise<void> {
    const id = photoLikeId(toUid, fromUid, photoId);
    await deleteDoc(doc(db, 'photoLikes', id));
  },

  /**
   * Photos of `toUid`'s profile that `fromUid` has liked. Used to render
   * like-state on someone else's profile.
   */
  subscribeToMyLikesOnProfile(
    fromUid: string,
    toUid: string,
    callback: (photoIds: Set<string>) => void,
    onError?: (error: Error) => void
  ) {
    const likesQuery = query(
      collection(db, 'photoLikes'),
      where('fromUid', '==', fromUid),
      where('toUid', '==', toUid)
    );
    return onSnapshot(
      likesQuery,
      snap => {
        const photoIds = new Set<string>();
        snap.forEach(d => photoIds.add(d.data().photoId));
        callback(photoIds);
      },
      error => {
        console.error('Profile likes subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback(new Set());
        }
      }
    );
  },

  /**
   * All likes received by `toUid`, newest first. `where('toUid', ...)` keeps
   * the list query authorized by the read rule.
   */
  subscribeToReceivedLikes(
    toUid: string,
    callback: (likes: PhotoLike[]) => void,
    onError?: (error: Error) => void
  ) {
    const likesQuery = query(
      collection(db, 'photoLikes'),
      where('toUid', '==', toUid),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(
      likesQuery,
      snap => {
        const likes: PhotoLike[] = [];
        snap.forEach(d => likes.push({ id: d.id, ...d.data() } as PhotoLike));
        callback(likes);
      },
      error => {
        console.error('Received likes subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback([]);
        }
      }
    );
  },
};

/** Likes whose `photoId` is still present in `photoIds` (drops orphans). */
export function filterOrphanLikes(likes: PhotoLike[], photoIds: Set<string>): PhotoLike[] {
  return likes.filter(like => photoIds.has(like.photoId));
}

/** Likes excluding any like involving a uid in `excludedUids` (blocks). */
export function filterBlockedLikes(likes: PhotoLike[], excludedUids: string[]): PhotoLike[] {
  if (excludedUids.length === 0) return likes;
  const excluded = new Set(excludedUids);
  return likes.filter(like => !excluded.has(like.fromUid) && !excluded.has(like.toUid));
}

/** Per-photo like counts keyed by `photoId`. */
export function countLikesByPhoto(likes: PhotoLike[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const like of likes) {
    counts.set(like.photoId, (counts.get(like.photoId) ?? 0) + 1);
  }
  return counts;
}
